/**
 * EventDetailsCard.js — Halleck Vineyard detailed event card.
 *
 * OVERRIDES: src/components/base/EventDetailsCard.js
 * REASON: Replicates the live createEventDetails: a light-grey card with a
 *   decorative eventdetail.png pinned top-right (NOT the cover image), a white
 *   rounded date pill, a bold heading, a divider, the FIRST description
 *   paragraph only, a price row, and a copper reserve button with the cta_text.
 *   Same DOM as the Goose detail card (ws-rich-detail-* structure); themed with
 *   Halleck colours in halleckvineyard/styles.css.
 *
 * INTERFACE: render(ev) → HTMLElement, identical signature to base.
 *
 * Reference: halleck_vineyard/script.js createEventDetails.
 */

import { ASSETS_BASE_URL } from "../../../core/env.js";

const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const PRICE_ICON =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M7 2h10l-1 7a4 4 0 0 1-3 3.9V19h3v2H8v-2h3v-6.1A4 4 0 0 1 8 9L7 2z"/></svg>';

const RESERVE_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 15 11 17 15 13"/></svg>';

export default class EventDetailsCard {
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-details-card ws-rich-event-details";

    // Decorative top image pinned top-right (live .detail-card-top-design →
    // eventdetail.png). NOT the event cover image.
    const top = document.createElement("div");
    top.className = "ws-rich-detail-top";
    const topImg = document.createElement("img");
    topImg.className = "ws-rich-detail-top-img";
    topImg.src = `${ASSETS_BASE_URL}/assets/halleckvineyard/eventdetail.png`;
    topImg.alt = "";
    top.appendChild(topImg);
    card.appendChild(top);

    if (ev.date) {
      const d = document.createElement("span");
      d.className = "ws-rich-detail-date";
      d.textContent = ev.date;
      card.appendChild(d);
    }

    if (ev.title) {
      const title = document.createElement("p");
      title.className = "ws-rich-detail-heading";
      title.title = ev.title;
      title.textContent = ev.title;
      card.appendChild(title);
    }

    const hr = document.createElement("div");
    hr.className = "ws-rich-detail-divider";
    card.appendChild(hr);

    // Description: live renders ONLY the first paragraph (description[0]).
    const desc = Array.isArray(ev.description) ? ev.description[0] : ev.description;
    if (desc) {
      const p = document.createElement("p");
      p.className = "ws-rich-detail-desc";
      p.textContent = desc;
      card.appendChild(p);
    }

    if (ev.price || ev.price_unit) {
      const priceRow = document.createElement("div");
      priceRow.className = "ws-rich-detail-price";
      priceRow.innerHTML =
        `<span class="ws-rich-detail-price-icon">${PRICE_ICON}</span>` +
        `<span class="ws-rich-detail-price-detail">${esc(ev.price || "")} <span class="ws-rich-detail-amount">${esc(ev.price_unit || "")}</span></span>`;
      card.appendChild(priceRow);
    }

    if (ev.cta_link) {
      const cta = document.createElement("a");
      cta.className = "ws-rich-detail-reserve";
      cta.href = ev.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.innerHTML =
        `<span class="ws-rich-detail-reserve-icon">${RESERVE_ICON}</span>` +
        `<span>${esc(ev.cta_text || "Reserve your spot")}</span>`;
      card.appendChild(cta);
    }

    return card;
  }
}
